import { Sparkles } from 'lucide-react';
import { ResizedImage } from '@ah/design/mobile';
import type { Circle } from '@ah/circles/shared';

/**
 * Coalition card — cause-led framing. Leads with the problem hook (an
 * evocative one-liner) and a short problem statement, then teases 2-3
 * active Campaigns so the member sees what the Coalition actually does.
 *
 * Yes opens the contribution sheet (skills / intros / funds / other) on
 * the host route, not the standard confirm dialog.
 */
export function CoalitionCard({ coalition }: { coalition: Circle }) {
  const campaigns = coalition.campaigns ?? [];
  const previewCampaigns = campaigns.slice(0, 3);

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      {coalition.coverUrl ? (
        <div className="w-full aspect-video bg-muted overflow-hidden">
          <ResizedImage src={coalition.coverUrl} renderWidth={384} fallback={false} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="w-full aspect-video bg-rose-500/85 flex items-center justify-center">
          <Sparkles size={40} className="text-white/80" />
        </div>
      )}

      <div className="p-6 space-y-5">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-primary mb-1">
            Underlings Coalitions
          </p>
          <p className="font-bold text-xl leading-tight">
            {coalition.displayTitle ?? coalition.name}
          </p>
          {coalition.problemHook && (
            <p className="text-[15px] text-foreground/85 leading-snug mt-3 italic">
              “{coalition.problemHook}”
            </p>
          )}
        </div>

        {coalition.problemStatement && (
          <p className="text-[15px] text-foreground/80 leading-relaxed">
            {coalition.problemStatement}
          </p>
        )}

        {previewCampaigns.length > 0 && (
          <div className="space-y-3">
            <p className="text-[13px] uppercase tracking-wider text-muted-foreground font-medium">
              Our Efforts
            </p>
            <ul className="space-y-3">
              {previewCampaigns.map((c) => (
                <li key={c.id} className="flex gap-2.5 text-[15px]">
                  <div className="w-1.5 h-1.5 rounded-full bg-rose-500/70 mt-2 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium leading-tight">{c.title}</p>
                    <p className="text-[14px] text-muted-foreground leading-snug mt-1">{c.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
